import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { dangNhapGoogle, theoDoiDangNhap } from '../lib/auth.js';
import { layDb } from '../lib/firebase.js';
import { PROGRAMS_GOC } from '../data/programs.js';
import { docTatCaChuongTrinh, luuChuongTrinh, xoaChuongTrinh, nhapTuCode } from '../lib/programs.js';
import {
  chuanHoaChuongTrinh, chuanHoaSlug, kiemTraChuongTrinh, canXemLai,
  LOAI_CHUONG_TRINH, LOAI_HO_TRO,
} from '../lib/programModel.js';
import { options } from '@shared/taxonomy.js';
import { homNay as layHomNay } from '../lib/format.js';

/**
 * /admin/chuong-trinh — thêm, sửa, ẩn chương trình / tài trợ mà không cần sửa code.
 *
 * Quyền: tài khoản có document admins/{uid} với role 'editor' (thêm, sửa, ẩn)
 * hoặc 'admin' (thêm cả quyền xoá vĩnh viễn). Xem HUONG-DAN-QUAN-TRI.md.
 */
const EMPTY = chuanHoaChuongTrinh({ kind: 'CORPORATE_ACCELERATOR', edition: { label: '' }, eligibility: { geos: ['VN'] } });
const NHAN_TT = { DRAFT: 'Bản nháp', PUBLISHED: 'Đang hiện', ARCHIVED: 'Đã ẩn' };

function useQuyenQuanTri() {
  const [s, setS] = useState({ user: null, role: '', checking: true });
  useEffect(() => theoDoiDangNhap(async (next) => {
    setS({ user: next, role: '', checking: true });
    let role = '';
    if (next) try {
      const db = await layDb();
      const { doc, getDoc } = await import('firebase/firestore/lite');
      const snap = await getDoc(doc(db, 'admins', next.uid));
      role = snap.exists() ? snap.data()?.role || '' : '';
    } catch { role = ''; }
    setS({ user: next, role, checking: false });
  }), []);
  return { ...s, allowed: ['admin', 'editor'].includes(s.role) };
}

const dong = (arr) => (arr || []).join('\n');
const tachDong = (s) => String(s || '').split('\n').map((x) => x.trim()).filter(Boolean);
const nguonThanhChu = (sources) => (sources || []).map((s) => (s.note ? `${s.url} | ${s.note}` : s.url)).join('\n');
const chuThanhNguon = (text, ngay) => tachDong(text).map((line) => {
  const [url, ...ghiChu] = line.split('|');
  return { url: url.trim(), note: ghiChu.join('|').trim(), fetchedAt: ngay };
});

function HopChon({ nhan, danhSach, chon, onDoi }) {
  return <div className="o-rong"><label>{nhan}</label><div className="admin-checks">
    {danhSach.map((o) => <label key={o.value}><input type="checkbox" checked={(chon || []).includes(o.value)}
      onChange={(e) => onDoi(o.value, e.target.checked)} /> {o.label}</label>)}
  </div></div>;
}

function XoaVinhVien({ slug, onXong, setMessage }) {
  const [mo, setMo] = useState(false); const [go, setGo] = useState(''); const [busy, setBusy] = useState(false);
  if (!mo) return <div className="admin-danger-toggle"><button type="button" className="text-button danger" onClick={() => setMo(true)}>Xoá vĩnh viễn…</button></div>;
  async function xoa() {
    setBusy(true);
    try { await xoaChuongTrinh(slug); setMessage('Đã xoá khỏi Firestore.'); onXong(); }
    catch (e) { setMessage(e?.code === 'permission-denied' ? 'Chỉ tài khoản role admin mới xoá được. Muốn gỡ khỏi website, dùng “Ẩn khỏi website”.' : e.message); }
    finally { setBusy(false); }
  }
  return <div className="admin-danger">
    <p><strong>Xoá vĩnh viễn “{slug}”?</strong></p>
    <p className="nho">Thường chỉ cần “Ẩn khỏi website” — giữ lại để biết kỳ trước. Nếu chương trình có sẵn trong code, xoá ở đây sẽ làm bản trong code hiện lại.</p>
    <label htmlFor="xoa-ct">Gõ lại <code>{slug}</code> để xác nhận</label>
    <input id="xoa-ct" type="text" value={go} autoComplete="off" onChange={(e) => setGo(e.target.value)} />
    <div className="admin-danger-actions">
      <button type="button" className="nut nut-nguy-hiem" disabled={go.trim() !== slug || busy} onClick={xoa}>{busy ? 'Đang xoá…' : 'Xoá vĩnh viễn'}</button>
      <button type="button" className="text-button" onClick={() => { setMo(false); setGo(''); }}>Huỷ</button>
    </div>
  </div>;
}

export default function ProgramAdmin() {
  const { user, role, allowed, checking } = useQuyenQuanTri();
  const [list, setList] = useState([]);
  const [draft, setDraft] = useState(EMPTY);
  const [nguonText, setNguonText] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [chiCanXem, setChiCanXem] = useState(false);
  const homNay = layHomNay();

  const daLuu = list.some((p) => p.slug === draft.slug);
  const slugNow = draft.slug || chuanHoaSlug(draft.name);
  const slugCuaCode = PROGRAMS_GOC.filter((p) => !list.some((x) => x.slug === p.slug));

  async function refresh() {
    try { setList((await docTatCaChuongTrinh()).sort((a, b) => a.name.localeCompare(b.name, 'vi'))); }
    catch (e) { setMessage(e.message); }
  }
  useEffect(() => { if (allowed) refresh(); }, [allowed]);

  const set = (k, v) => setDraft((o) => ({ ...o, [k]: v }));
  const setIn = (k, sub, v) => setDraft((o) => ({ ...o, [k]: { ...(o[k] || {}), [sub]: v } }));
  const batIn = (k, sub, v, on) => setDraft((o) => {
    const cu = o[k]?.[sub] || [];
    return { ...o, [k]: { ...(o[k] || {}), [sub]: on ? [...new Set([...cu, v])] : cu.filter((x) => x !== v) } };
  });

  function mo(p) {
    setDraft(p); setNguonText(nguonThanhChu(p.provenance?.sources)); setMessage('');
    window.scrollTo(0, 0);
  }
  function moi() { setDraft(EMPTY); setNguonText(''); setMessage(''); }

  const banGhi = useMemo(() => chuanHoaChuongTrinh({
    ...draft,
    slug: slugNow,
    provenance: { ...(draft.provenance || {}), sources: chuThanhNguon(nguonText, draft.provenance?.lastVerifiedAt || homNay) },
  }), [draft, slugNow, nguonText, homNay]);
  const loi = kiemTraChuongTrinh(banGhi);

  async function luu(status) {
    if (!daLuu && PROGRAMS_GOC.some((p) => p.slug === slugNow)) {
      setMessage(`Đường dẫn "${slugNow}" là chương trình có sẵn trong code. Bấm “Chép chương trình có sẵn vào đây” rồi sửa bản đó.`);
      return;
    }
    if (!daLuu && list.some((p) => p.slug === slugNow)) { setMessage('Đường dẫn này đã có. Hãy đổi đường dẫn.'); return; }
    if (status === 'PUBLISHED' && loi.length) { setMessage('Chưa xuất bản được: ' + loi.join(' ')); return; }
    setBusy(true); setMessage('Đang lưu…');
    try {
      const saved = await luuChuongTrinh(banGhi, status, user?.email || '');
      setDraft(chuanHoaChuongTrinh(saved));
      setMessage({
        PUBLISHED: 'Đã xuất bản. Trang /chuong-trinh hiện ngay; bản HTML cho Google cập nhật ở lần deploy kế tiếp.',
        DRAFT: 'Đã lưu bản nháp (chưa hiện trên website).',
        ARCHIVED: 'Đã ẩn khỏi website. Bấm “Xuất bản” để hiện lại.',
      }[status]);
      refresh();
    } catch (e) {
      setMessage(e?.code === 'permission-denied' ? 'Firestore từ chối lưu. Kiểm tra quyền của tài khoản và Firestore Rules đã deploy chưa.' : e.message);
    } finally { setBusy(false); }
  }

  async function nhap() {
    setBusy(true); setMessage('Đang chép chương trình có sẵn…');
    try { const n = await nhapTuCode(list.map((p) => p.slug), user?.email || ''); setMessage(`Đã chép ${n} chương trình. Từ giờ sửa chúng ngay trên trang này.`); refresh(); }
    catch (e) { setMessage(e?.code === 'permission-denied' ? 'Firestore từ chối lưu (kiểm tra Rules đã deploy chưa).' : e.message); }
    finally { setBusy(false); }
  }

  function kyMoi() {
    const nam = Number(homNay.slice(0, 4)) + (draft.edition?.closesAt && draft.edition.closesAt < homNay ? 1 : 0);
    setDraft((o) => ({ ...o, edition: { label: `Kỳ ${nam}`, rolling: false, opensAt: null, closesAt: null } }));
    setMessage('Đã xoá lịch cũ. Điền ngày của kỳ mới, cập nhật nguồn và ngày kiểm chứng rồi bấm “Xuất bản”.');
  }

  if (checking) return <div className="surface section-space"><p>Đang kiểm tra quyền quản trị…</p></div>;
  if (!user) return <section className="surface admin-gate"><h1>Quản trị chương trình</h1><p>Đăng nhập bằng tài khoản đã được cấp quyền.</p><button className="nut" onClick={() => dangNhapGoogle()}>Đăng nhập với Google</button></section>;
  if (!allowed) return <section className="surface admin-gate"><h1>Chưa có quyền quản trị</h1><p>Tài khoản <strong>{user.email}</strong> chưa nằm trong danh sách admin.</p><p className="nho">Gửi mã này cho quản trị viên chính để được cấp quyền: <code>{user.uid}</code></p></section>;

  const e = draft.edition || {};
  const hienThi = list.filter((p) => !chiCanXem || (p.status === 'PUBLISHED' && canXemLai(p, homNay)));
  const soCanXem = list.filter((p) => p.status === 'PUBLISHED' && canXemLai(p, homNay)).length;

  return <div className="admin-playbook">
    <header className="page-heading"><p className="eyebrow">Khu vực quản trị · <Link to="/admin/playbook">Playbook</Link> · <Link to="/admin/mau">Thư viện mẫu</Link> · Chương trình · <Link to="/admin/nguon-von">Nguồn vốn</Link></p>
      <h1>Chương trình và tài trợ</h1>
      <p>Thêm chương trình mới, cập nhật lịch kỳ mới hoặc ẩn chương trình. Thay đổi hiện ngay trên <Link to="/chuong-trinh">/chuong-trinh</Link>.</p></header>
    <div className="admin-layout">
      <aside className="surface admin-list">
        <div className="hang-nut"><button className="nut" onClick={moi}>+ Chương trình mới</button></div>
        {soCanXem > 0 && <p className="nho"><label><input type="checkbox" checked={chiCanXem} onChange={(ev) => setChiCanXem(ev.target.checked)} /> Chỉ hiện {soCanXem} mục cần xem lại</label></p>}
        {hienThi.length === 0 && <p className="nho">{list.length ? 'Không có mục nào cần xem lại.' : 'Chưa có chương trình nào trên trang quản trị.'}</p>}
        {hienThi.map((p) => {
          const nhac = p.status === 'PUBLISHED' ? canXemLai(p, homNay) : '';
          return <button className="admin-post-row" key={p.slug} onClick={() => mo(p)}>
            <strong>{p.name || p.slug}</strong>
            <span>{NHAN_TT[p.status]} · {p.edition?.label || '—'}{p.edition?.closesAt ? ` · hạn ${p.edition.closesAt}` : ''}</span>
            {nhac && <span className="badge-canh-bao">{nhac}</span>}
          </button>;
        })}
        {slugCuaCode.length > 0 && <>
          <h2>Có sẵn trong code</h2>
          <p className="nho">{slugCuaCode.length} chương trình đang hiện trên website nhưng chưa sửa được ở đây.</p>
          <button className="nut phu" disabled={busy} onClick={nhap}>Chép chương trình có sẵn vào đây</button>
          <ul className="nho">{slugCuaCode.map((p) => <li key={p.slug}>{p.name}</li>)}</ul>
        </>}
      </aside>

      <section className="surface admin-editor">
        <p role="status" className="admin-message">{message}</p>
        <div className="o-nhap admin-fields">
          <div className="o-rong"><label>Tên chương trình</label><input type="text" value={draft.name} placeholder="Google for Startups Accelerator: Đông Nam Á" onChange={(ev) => set('name', ev.target.value)} /></div>
          <div><label>Tên ngắn</label><input type="text" value={draft.shortName} onChange={(ev) => set('shortName', ev.target.value)} /></div>
          <div><label>Đường dẫn {daLuu && <span className="nho">(đã khoá)</span>}</label>
            <input type="text" value={draft.slug} placeholder={chuanHoaSlug(draft.name)} disabled={daLuu} onChange={(ev) => set('slug', chuanHoaSlug(ev.target.value))} /></div>
          <div><label>Đơn vị tổ chức</label><input type="text" value={draft.organizer} onChange={(ev) => set('organizer', ev.target.value)} /></div>
          <div><label>Loại chương trình</label><select value={draft.kind} onChange={(ev) => set('kind', ev.target.value)}>{LOAI_CHUONG_TRINH.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}</select></div>
          <div><label>Website</label><input type="url" value={draft.website} placeholder="https://…" onChange={(ev) => set('website', ev.target.value)} /></div>
          <div><label>Logo (đường dẫn ảnh, tuỳ chọn)</label><input type="url" value={draft.logoUrl} placeholder="https://…" onChange={(ev) => set('logoUrl', ev.target.value)} /></div>

          <div className="o-rong"><h2>Đợt hiện tại</h2>
            <label><input type="checkbox" checked={Boolean(e.rolling)} onChange={(ev) => setIn('edition', 'rolling', ev.target.checked)} /> Nhận hồ sơ liên tục (không có hạn nộp)</label>
            {daLuu && !e.rolling && <p><button type="button" className="text-button" onClick={kyMoi}>Chuẩn bị kỳ mới (xoá ngày cũ)</button></p>}
          </div>
          <div><label>Tên đợt</label><input type="text" value={e.label || ''} placeholder="Kỳ 2027, Cohort 9…" onChange={(ev) => setIn('edition', 'label', ev.target.value)} /></div>
          {!e.rolling && <>
            <div><label>Ngày mở nhận hồ sơ</label><input type="date" value={e.opensAt || ''} onChange={(ev) => setIn('edition', 'opensAt', ev.target.value || null)} /></div>
            <div><label>Hạn nộp</label><input type="date" value={e.closesAt || ''} onChange={(ev) => setIn('edition', 'closesAt', ev.target.value || null)} /></div>
          </>}

          <div className="o-rong"><h2>Startup nhận được gì</h2></div>
          <HopChon nhan="Loại hỗ trợ" danhSach={LOAI_HO_TRO} chon={draft.benefit?.capitalTypes} onDoi={(v, on) => batIn('benefit', 'capitalTypes', v, on)} />
          <div className="o-rong"><label>Mô tả quyền lợi</label><textarea rows="3" value={draft.benefit?.note || ''} onChange={(ev) => setIn('benefit', 'note', ev.target.value)} /></div>

          <div className="o-rong"><h2>Ai được nộp</h2></div>
          <HopChon nhan="Giai đoạn" danhSach={options('STAGE')} chon={draft.eligibility?.stages} onDoi={(v, on) => batIn('eligibility', 'stages', v, on)} />
          <HopChon nhan="Địa bàn" danhSach={options('GEO')} chon={draft.eligibility?.geos} onDoi={(v, on) => batIn('eligibility', 'geos', v, on)} />
          <HopChon nhan="Ngành (để trống nếu không giới hạn)" danhSach={options('INDUSTRY')} chon={draft.eligibility?.industries} onDoi={(v, on) => batIn('eligibility', 'industries', v, on)} />
          <div className="o-rong"><label>Điều kiện bắt buộc (mỗi dòng một điều kiện)</label><textarea rows="4" value={dong(draft.eligibility?.hardRequirements)} onChange={(ev) => setIn('eligibility', 'hardRequirements', ev.target.value.split('\n'))} /></div>

          <div className="o-rong"><h2>Nội dung hiển thị</h2></div>
          <div className="o-rong"><label>Mô tả ngắn</label><textarea rows="3" value={draft.content?.summary || ''} onChange={(ev) => setIn('content', 'summary', ev.target.value)} /></div>
          <div className="o-rong"><label>Lưu ý cho founder (mỗi dòng một ý, tuỳ chọn)</label><textarea rows="3" value={dong(draft.content?.tips)} onChange={(ev) => setIn('content', 'tips', ev.target.value.split('\n'))} /></div>
          <div className="o-rong"><label>Đường dẫn nộp hồ sơ / hướng dẫn chính thức</label><input type="url" value={draft.process?.applyUrl || ''} placeholder="https://…" onChange={(ev) => setIn('process', 'applyUrl', ev.target.value)} /></div>

          <div className="o-rong"><h2>Nguồn và kiểm chứng</h2>
            <p className="nho">Chỉ điền thông tin trang chính thức nói rõ. Mỗi dòng một nguồn, dạng <code>https://… | ghi chú</code>.</p></div>
          <div className="o-rong"><label>Nguồn</label><textarea rows="3" value={nguonText} placeholder="https://… | Thông báo chính thức kỳ 2027" onChange={(ev) => setNguonText(ev.target.value)} /></div>
          <div><label>Ngày kiểm chứng</label><input type="date" value={draft.provenance?.lastVerifiedAt || ''} onChange={(ev) => setIn('provenance', 'lastVerifiedAt', ev.target.value || null)} />
            <button type="button" className="text-button" onClick={() => setIn('provenance', 'lastVerifiedAt', homNay)}>Hôm nay</button></div>
        </div>

        {loi.length > 0 && <div className="notice"><strong>Cần bổ sung trước khi xuất bản:</strong><ul>{loi.map((x) => <li key={x}>{x}</li>)}</ul></div>}
        {draft.updatedAt && <p className="nho">Sửa lần cuối: {new Date(draft.updatedAt).toLocaleString('vi-VN')}{draft.updatedBy ? ` · ${draft.updatedBy}` : ''} · Trạng thái: {NHAN_TT[draft.status]}</p>}
        <div className="hang-nut">
          <button className="nut" disabled={busy} onClick={() => luu('PUBLISHED')}>Xuất bản</button>
          <button className="nut phu" disabled={busy} onClick={() => luu('DRAFT')}>Lưu nháp</button>
          {daLuu && draft.status !== 'ARCHIVED' && <button className="nut phu" disabled={busy} onClick={() => luu('ARCHIVED')}>Ẩn khỏi website</button>}
        </div>
        {daLuu && role === 'admin' && <XoaVinhVien slug={draft.slug} setMessage={setMessage} onXong={() => { moi(); refresh(); }} />}
      </section>
    </div>
  </div>;
}
